"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ShoppingCart } from "lucide-react";

import { Sheet, SheetContent, SheetFooter, SheetTrigger } from "@fluid-app/ui";

import {
  CART_SHEET_EVENTS,
  dispatchCartEvent,
  generateWidgetId,
  initializeWithLocalCart,
} from "../utils";
import ShadowDOMWrapper from "../utils/ShadowDOMWrapper";
import { useFluidSettings } from "../utils/widgetSettings";

import "../styles/globals.css";

import type { getCart } from "@fluid-app/fluid";
import type { CartWidgetProps, Schemas } from "@fluid-app/react-widgets-types";

import CartSidebarContent, {
  getTotalQuantityFromCart,
  isEnrollmentPackItem,
} from "./CartSidebarContent";
import { defaultMockCart } from "./mockData";

type CartData = Awaited<ReturnType<typeof getCart>>;

/**
 * Custom hook to detect mobile devices
 */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    // Initialize with a proper check on first render
    if (typeof window === "undefined") return false;

    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileUserAgent =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent,
      );
    const isSmallScreen = window.innerWidth < 768; // Tailwind's md breakpoint

    // Prioritize mobile user agent detection, then fall back to screen size
    return isMobileUserAgent || isSmallScreen;
  });

  useEffect(() => {
    // Only listen to resize events if we need to check screen size
    // Mobile user agents don't need resize listeners
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileUserAgent =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent,
      );

    // If it's definitely a mobile user agent, no need to listen to resize
    if (isMobileUserAgent) {
      setIsMobile(true);
      return;
    }

    const checkIsMobile = () => {
      const isSmallScreen = window.innerWidth < 768; // Tailwind's md breakpoint

      const newIsMobile = isSmallScreen;

      // Only update state if the value actually changed
      setIsMobile((prevIsMobile) => {
        if (prevIsMobile !== newIsMobile) {
          return newIsMobile;
        }
        return prevIsMobile;
      });
    };

    // Use a debounced resize handler to prevent excessive re-renders
    let timeoutId: number;
    const debouncedCheckIsMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkIsMobile, 250); // Increased debounce time
    };

    // Check on resize with debouncing
    window.addEventListener("resize", debouncedCheckIsMobile);

    return () => {
      window.removeEventListener("resize", debouncedCheckIsMobile);
      clearTimeout(timeoutId);
    };
  }, []);

  return isMobile;
}

/**
 * Preload cart widget styles
 * This can be called early in the application to ensure styles
 * are ready when components are mounted
 */
export function preloadCartWidgetStyles(): void {
  // Skip in server-side rendering
  if (typeof window === "undefined") return;

  // Use window to check global Fluid settings
  interface FluidWindow extends Window {
    __FLUID_STYLE_URL?: string;
  }
  const fluidWindow = window as unknown as FluidWindow;

  // Make sure we have a style URL set
  fluidWindow.__FLUID_STYLE_URL ??= "/styles.css";

  // Import shadow DOM style utilities and preload styles
  try {
    import("../utils/shadowDomStyles")
      .then((module) => {
        // Create a placeholder shadow root to initialize styles
        const tempDiv = document.createElement("div");
        const tempShadow = tempDiv.attachShadow({ mode: "open" });

        // Apply styles to initialize caching
        module.applyStylesIntoShadowRoot(tempShadow);
      })
      .catch((err) => {
        console.warn("Failed to preload cart widget styles:", err);
      });
  } catch (error: unknown) {
    console.warn("Could not import shadow DOM style utilities:", error);
  }
}

const CartWidget = ({
  position = "bottom_right",
  size = 60,
  xMargin = 16,
  yMargin = 16,
  fluidShopDisplayName,
  hideWidget = false,
  demoMode = false,
  demoCart = null,
  alwaysOpen = false,
  overrideCart = null,
  onIncrementCart,
  onDecrementCart,
  onRemoveEnrollmentPack,
  onEnrollmentPackAdd,
  onCheckout,
  primaryColor,
}: CartWidgetProps) => {
  // Check for server-side rendering early
  if (typeof window === "undefined") {
    return null; // Return null during server-side rendering
  }

  const {
    addCartItems,
    checkout,
    decrementCartItem,
    getCart,
    getLocalCart,
    removeCartItemById,
  } = window.FluidCommerceSDK;

  // Use mobile detection hook
  const isMobile = useIsMobile();

  // Memoize isMobile to prevent unnecessary re-renders
  const stableIsMobile = useMemo(() => isMobile, [isMobile]);

  // Use the Fluid settings hook
  const { getDisplayNames, isLoading } = useFluidSettings();

  // Get the display names with proper priority
  const { shopDisplayName, affiliateDisplayName } =
    getDisplayNames(fluidShopDisplayName);

  // Use the effective shop display name based on priority
  const effectiveShopDisplayName = shopDisplayName;

  // Get initial cart data if available or use demoCart in demo mode
  const initialLocalCart = useMemo((): CartData => {
    if (demoMode) {
      // Use provided demoCart or default mock cart
      return demoCart ?? defaultMockCart;
    }
    if (overrideCart) {
      return overrideCart as CartData;
    }
    return initializeWithLocalCart() as CartData;
  }, [demoMode, demoCart, overrideCart]);

  // State
  const [isOpen, setIsOpen] = useState(alwaysOpen);
  const [cart, setCart] = useState<CartData>(
    initialLocalCart ?? undefined, // Initialize with local cart data if available
  );

  useEffect(() => {
    if (initialLocalCart) {
      setCart(initialLocalCart);
    }
  }, [initialLocalCart]);

  const [itemCount, setItemCount] = useState(
    getTotalQuantityFromCart(initialLocalCart ?? undefined),
  );

  // Add loading state for cart items
  const [isCartLoading, setIsCartLoading] = useState(false);

  // Add loading state for checkout price
  const [isCheckoutPriceLoading, setIsCheckoutPriceLoading] = useState(false);

  // Add loading states for individual item operations
  const [itemLoadingStates, setItemLoadingStates] = useState<
    Record<number, boolean>
  >({});

  // Check if any item is currently loading
  const isAnyItemLoading = useMemo(
    () => Object.values(itemLoadingStates).some(Boolean),
    [itemLoadingStates],
  );

  // Track animation state with refs to prevent double animations
  const shouldShowAnimationRef = useRef(true);
  const animationTimeoutRef = useRef<number | null>(null);

  // Create a ref to store the shadow root
  const shadowRootRef = useRef<ShadowRoot | null>(null);

  // Generate a stable widget ID once for this component instance
  const widgetIdRef = useRef<string>(generateWidgetId("cart-widget"));

  // Track if CREATE_CART is in progress to prevent CART_LOADING_END interference
  const isCreateCartInProgress = useRef(false);

  // Track if enrollment pack operations are in progress
  const isEnrollmentOperationInProgress = useRef(false);

  const hasItems = useMemo(() => !!cart?.items?.length, [cart?.items?.length]);

  // Control animation timing - only start when not loading
  useEffect(() => {
    // Only start animation timing when not loading
    if (isLoading) return;

    // Clear any existing timer
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    // Only set up the timer if animation should be shown
    if (shouldShowAnimationRef.current) {
      animationTimeoutRef.current = setTimeout(() => {
        shouldShowAnimationRef.current = false;
        animationTimeoutRef.current = null;
      }, 1000); // After the initial animation would have completed
    }

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, [isLoading]); // Add isLoading as dependency

  // Function to get the current cart
  const getCartWithFallback = async (): Promise<CartData> => {
    if (demoMode) return cart;

    try {
      // Check if SDK is initialized before trying to get local cart
      // if (
      //   typeof window !== "undefined" &&
      //   !(window as CustomWindow).FLUID_SDK_INITIALIZED
      // ) {
      //   // SDK not initialized yet, skip local cart and go straight to API
      //   return await getCart();
      // }

      // Try to get cached data first
      const cachedCart = getLocalCart();
      if (cachedCart) {
        return cachedCart;
      }

      // Fall back to API call if no cached data
      return await getCart();
    } catch (e) {
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to get cart:`,
        e,
      );
      return undefined;
    }
  };

  // Throttle cart refreshes to avoid excessive API calls
  const lastRefreshTime = useRef<number>(0);
  const refreshThrottleMs = 1000;

  // Refresh cart data from API (force refresh when needed)
  const refreshCart = async (forceApiCall = false) => {
    if (overrideCart) return;
    // Skip in demo mode
    if (demoMode) return;

    // Throttle API calls - don't refresh more than once per 500ms
    const now = Date.now();
    if (!forceApiCall && now - lastRefreshTime.current < refreshThrottleMs) {
      return;
    }
    lastRefreshTime.current = now;

    try {
      let cartData: CartData;

      if (forceApiCall) {
        // Force API call
        cartData = await getCart();
      } else {
        // Use smart fallback (cached first, then API)
        cartData = await getCartWithFallback();
      }

      if (cartData) {
        setCart(cartData);
        setItemCount(getTotalQuantityFromCart(cartData));
      }
    } catch (error: unknown) {
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to refresh cart:`,
        error,
      );
    }
  };

  // Initial cart load
  useEffect(() => {
    // Skip SDK initialization in demo mode
    if (demoMode) return;

    // Use a ref to track if this effect is still valid (component hasn't unmounted)
    const abortController = new AbortController();

    // Set up event listeners for cart updates from other components
    const handleCartUpdate = async (event: Event) => {
      // Don't process events if component is unmounting
      if (abortController.signal.aborted) return;

      // Properly type the custom event
      const cartEvent = event as CustomEvent<
        | {
            handledByWidget: boolean;
            widgetId: string;
            cart?: Schemas["Cart"]; // Cart data from FluidCore
          }
        | null
        | undefined
      >;

      // Check if this is our own event to prevent loops
      const eventDetail = cartEvent.detail;
      if (
        eventDetail &&
        eventDetail.handledByWidget &&
        eventDetail.widgetId === widgetIdRef.current
      ) {
        return;
      }

      // For CREATE_CART or ADD_TO_CART events, use cart data from event if available
      if (event.type === "CREATE_CART" || event.type === "ADD_TO_CART") {
        isCreateCartInProgress.current = true; // Track that cart operation is in progress
        setIsCartLoading(true); // Ensure loading state while we update

        // Check if cart data was provided with the event (eliminates redundant API call)
        if (eventDetail?.cart) {
          // Use cart data directly from the event
          setCart(eventDetail.cart);
          setItemCount(getTotalQuantityFromCart(eventDetail.cart));

          isCreateCartInProgress.current = false; // Clear tracking

          // For enrollment operations, DON'T clear enrollment tracking yet - let CART_LOADING_END handle it
          // This prevents ending loading state prematurely before loading events finish
          if (!isEnrollmentOperationInProgress.current) {
            // Only end loading if this isn't an enrollment operation
            setIsCartLoading(false);
          }

          return; // Skip API call since we have the data
        }
      }

      // For non-CREATE_CART/ADD_TO_CART events or those events without data, refresh from cache first
      try {
        // For CREATE_CART events without data, force API call to get fresh data
        // For other events, use cached data first (since they're usually optimistic updates)
        const forceApiCall = event.type === "CREATE_CART";
        await refreshCart(forceApiCall);

        // Only end loading for CREATE_CART or ADD_TO_CART after successful refresh
        if (event.type === "CREATE_CART" || event.type === "ADD_TO_CART") {
          isCreateCartInProgress.current = false; // Clear tracking

          // For enrollment operations, DON'T clear enrollment tracking yet - let CART_LOADING_END handle it
          if (!isEnrollmentOperationInProgress.current) {
            // Only end loading if this isn't an enrollment operation
            setIsCartLoading(false);
          }
        }
      } catch (error: unknown) {
        console.error(
          `Cart widget [${widgetIdRef.current}]: Failed to refresh cart on event:`,
          error,
        );

        // End loading even on error to prevent stuck state
        if (event.type === "CREATE_CART" || event.type === "ADD_TO_CART") {
          isCreateCartInProgress.current = false; // Clear tracking

          // For enrollment operations, DON'T clear enrollment tracking yet - let CART_LOADING_END handle it
          if (!isEnrollmentOperationInProgress.current) {
            // Only end loading if this isn't an enrollment operation
            setIsCartLoading(false);
          }
        }
      }
    };

    // Create wrapper functions to handle async properly
    const handleCartUpdateWrapper = (event: Event) => {
      void handleCartUpdate(event);
    };

    // Only add event listeners if component is still mounted
    window.addEventListener("ADD_TO_CART", handleCartUpdateWrapper);
    window.addEventListener("CREATE_CART", handleCartUpdateWrapper);
    window.addEventListener("UPDATE_CART_ITEM", handleCartUpdateWrapper);
    window.addEventListener("REMOVE_FROM_CART", handleCartUpdateWrapper);
    window.addEventListener("LOCALE_CHANGE", handleCartUpdateWrapper);

    // Store event cleanup functions
    const removeEventListeners = () => {
      window.removeEventListener("ADD_TO_CART", handleCartUpdateWrapper);
      window.removeEventListener("CREATE_CART", handleCartUpdateWrapper);
      window.removeEventListener("UPDATE_CART_ITEM", handleCartUpdateWrapper);
      window.removeEventListener("REMOVE_FROM_CART", handleCartUpdateWrapper);
      window.removeEventListener("LOCALE_CHANGE", handleCartUpdateWrapper);
    };

    // Initial cart load - use cached data first, then API if needed
    void refreshCart(false);

    return () => {
      abortController.abort();
      removeEventListeners();
    };
  }, [demoMode, getCart, refreshCart, getTotalQuantityFromCart]);

  // Listen for cart loading events
  useEffect(() => {
    const handleCartLoadingStart = (event: Event) => {
      setIsCartLoading(true);

      // Check if this is enrollment-related loading
      const customEvent = event as CustomEvent<{ type?: string } | null>;
      if (
        customEvent.detail?.type === "enrollment" ||
        customEvent.detail?.type === "combined"
      ) {
        isEnrollmentOperationInProgress.current = true;
      }
    };

    const handleCartLoadingEnd = (event: Event) => {
      // Check if this is enrollment-related loading
      const customEvent = event as CustomEvent<{ type?: string } | null>;
      if (
        customEvent.detail?.type === "enrollment" ||
        customEvent.detail?.type === "combined"
      ) {
        isEnrollmentOperationInProgress.current = false;
      }

      // Don't end loading if CREATE_CART or enrollment operations are in progress
      if (
        !isCreateCartInProgress.current &&
        !isEnrollmentOperationInProgress.current
      ) {
        setIsCartLoading(false);
      }
    };

    // Add event listeners for cart loading events
    window.addEventListener(
      CART_SHEET_EVENTS.CART_LOADING_START,
      handleCartLoadingStart,
    );
    window.addEventListener(
      CART_SHEET_EVENTS.CART_LOADING_END,
      handleCartLoadingEnd,
    );

    // Clean up event listeners
    return () => {
      window.removeEventListener(
        CART_SHEET_EVENTS.CART_LOADING_START,
        handleCartLoadingStart,
      );
      window.removeEventListener(
        CART_SHEET_EVENTS.CART_LOADING_END,
        handleCartLoadingEnd,
      );
    };
  }, []);

  // Listen for cart sheet control events
  useEffect(() => {
    const handleOpenCart = async () => {
      setIsOpen(true);
      // Only refresh if cart was recently updated (within last 2 seconds) or if we have no items
      if (
        !demoMode &&
        (Date.now() - lastRefreshTime.current < 2000 || !hasItems)
      ) {
        await refreshCart(true); // Force API call to get fresh data
      }
    };

    const handleCloseCart = () => {
      setIsOpen(false);
    };

    const handleToggleCart = async () => {
      const newOpenState = !isOpen;
      setIsOpen(newOpenState);
      // Only refresh if cart was recently updated (within last 2 seconds) or if we have no items
      if (
        newOpenState &&
        !demoMode &&
        (Date.now() - lastRefreshTime.current < 2000 || !hasItems)
      ) {
        await refreshCart(true); // Force API call to get fresh data
      }
    };

    // Create wrapper functions to handle async properly
    const handleOpenCartWrapper = (_event: Event) => {
      void handleOpenCart();
    };

    const handleToggleCartWrapper = (_event: Event) => {
      void handleToggleCart();
    };

    // Add event listeners for cart sheet events
    window.addEventListener(CART_SHEET_EVENTS.OPEN_CART, handleOpenCartWrapper);
    window.addEventListener(CART_SHEET_EVENTS.CLOSE_CART, handleCloseCart);
    window.addEventListener(
      CART_SHEET_EVENTS.TOGGLE_CART,
      handleToggleCartWrapper,
    );

    // Clean up by removing event listeners
    return () => {
      window.removeEventListener(
        CART_SHEET_EVENTS.OPEN_CART,
        handleOpenCartWrapper,
      );
      window.removeEventListener(CART_SHEET_EVENTS.CLOSE_CART, handleCloseCart);
      window.removeEventListener(
        CART_SHEET_EVENTS.TOGGLE_CART,
        handleToggleCartWrapper,
      );
    };
  }, []);

  // Handler for incrementing item quantity
  const handleIncrement = async (variantId: number): Promise<void> => {
    if (!cart?.items) return;

    if (onIncrementCart && typeof onIncrementCart === "function") {
      (onIncrementCart as (variantId: number) => void)(variantId);
      return;
    }

    // Only find non-enrollment items with the matching variant_id
    const item = cart.items.find(
      (i: Schemas["CartItem"]) =>
        i.variant?.id === variantId && !isEnrollmentPackItem(i),
    );
    if (!item) return;

    // Set loading state for this specific item
    setItemLoadingStates((prev) => ({ ...prev, [variantId]: true }));

    // If demo mode, update state directly
    if (demoMode) {
      const currentQuantity = Number(item.quantity) || 0;
      const updatedItems = cart.items.map((i: Schemas["CartItem"]) =>
        i.variant?.id === variantId && !isEnrollmentPackItem(i)
          ? { ...i, quantity: currentQuantity + 1 }
          : i,
      );
      const updatedCart = {
        ...cart,
        items: updatedItems,
        recurring: cart.recurring,
      };
      setCart(updatedCart);
      setItemCount(getTotalQuantityFromCart(updatedCart));

      // Dispatch local event for tracking in demo mode
      dispatchCartEvent(
        "UPDATE_CART_ITEM",
        {
          itemId: variantId,
          quantity: currentQuantity + 1,
          action: "increment",
          itemDetails: item,
          timestamp: new Date().toISOString(),
          demoMode: true,
        },
        widgetIdRef.current,
      );

      // Remove loading state
      setTimeout(() => {
        setItemLoadingStates((prev) => ({ ...prev, [variantId]: false }));
      }, 300);
      return;
    }

    try {
      // Call SDK function and wait for response
      await addCartItems(variantId, { quantity: 1 });

      // Dispatch event for tracking
      dispatchCartEvent(
        "UPDATE_CART_ITEM",
        {
          itemId: variantId,
          quantity: (Number(item.quantity) || 0) + 1,
          action: "increment",
          itemDetails: item,
          timestamp: new Date().toISOString(),
        },
        widgetIdRef.current,
      );

      // Refresh cart from SDK
      const freshCart = await getCartWithFallback();
      if (freshCart) {
        setCart(freshCart);
        setItemCount(getTotalQuantityFromCart(freshCart));
        localStorage.setItem("cart_last_updated", Date.now().toString());
        lastRefreshTime.current = Date.now(); // Update last refresh time
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to increment item ${variantId}:`,
        errorMessage,
      );
    } finally {
      // Remove loading state
      setItemLoadingStates((prev) => ({ ...prev, [variantId]: false }));
    }
  };

  // Handler for decrementing item quantity
  const handleDecrement = async (variantId: number): Promise<void> => {
    if (!cart?.items) return;

    if (onDecrementCart && typeof onDecrementCart === "function") {
      (onDecrementCart as (variantId: number) => void)(variantId);
      return;
    }

    // Only find non-enrollment items with the matching variant_id
    const item = cart.items.find(
      (i: Schemas["CartItem"]) =>
        i.variant?.id === variantId && !isEnrollmentPackItem(i),
    );
    if (!item) return;

    const currentQuantity = Number(item.quantity) || 0;
    if (currentQuantity <= 0) return;

    // Set loading state for this specific item
    setItemLoadingStates((prev) => ({ ...prev, [variantId]: true }));

    // If demo mode, update state directly
    if (demoMode) {
      const isRemoval = currentQuantity === 1;
      const newQuantity = currentQuantity - 1;
      let updatedItems;
      if (isRemoval) {
        // Remove the item entirely
        updatedItems = cart.items.filter(
          (i: Schemas["CartItem"]) =>
            i.variant?.id !== variantId || isEnrollmentPackItem(i),
        );
      } else {
        // Decrement quantity
        updatedItems = cart.items.map((i: Schemas["CartItem"]) =>
          i.variant?.id === variantId && !isEnrollmentPackItem(i)
            ? { ...i, quantity: newQuantity }
            : i,
        );
      }
      const updatedCart = {
        ...cart,
        items: updatedItems,
        recurring: cart.recurring,
      };
      setCart(updatedCart);
      setItemCount(getTotalQuantityFromCart(updatedCart));

      // Dispatch local event for tracking in demo mode
      dispatchCartEvent(
        isRemoval ? "REMOVE_FROM_CART" : "UPDATE_CART_ITEM",
        {
          itemId: isRemoval ? item.id : variantId,
          quantity: isRemoval ? undefined : newQuantity,
          action: "decrement",
          itemDetails: item,
          timestamp: new Date().toISOString(),
          demoMode: true,
        },
        widgetIdRef.current,
      );

      // Remove loading state
      setTimeout(() => {
        setItemLoadingStates((prev) => ({ ...prev, [variantId]: false }));
      }, 300);
      return;
    }

    try {
      // Call SDK function and wait for response
      await decrementCartItem(variantId, { quantity: 1 });

      const isRemoval = currentQuantity === 1;
      // Dispatch event for tracking
      dispatchCartEvent(
        isRemoval ? "REMOVE_FROM_CART" : "UPDATE_CART_ITEM",
        {
          itemId: isRemoval ? item.id : variantId,
          quantity: isRemoval ? undefined : currentQuantity - 1,
          action: "decrement",
          itemDetails: item,
          timestamp: new Date().toISOString(),
        },
        widgetIdRef.current,
      );

      // Refresh cart from SDK
      const freshCart = await getCartWithFallback();
      if (freshCart) {
        setCart(freshCart);
        setItemCount(getTotalQuantityFromCart(freshCart));
        localStorage.setItem("cart_last_updated", Date.now().toString());
        lastRefreshTime.current = Date.now(); // Update last refresh time
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to decrement item ${variantId}:`,
        errorMessage,
      );
    } finally {
      // Remove loading state
      setItemLoadingStates((prev) => ({ ...prev, [variantId]: false }));
    }
  };

  // Handle checkout
  const handleCheckout = async (): Promise<void> => {
    // If custom checkout handler is provided, use it
    if (onCheckout) {
      setIsCheckoutPriceLoading(true);
      await onCheckout();
      setIsCheckoutPriceLoading(false);
      return;
    }

    try {
      // Set loading state to true when checkout starts
      setIsCheckoutPriceLoading(true);

      // In demo mode, just log the checkout action
      if (demoMode) {
        console.log("Demo mode: Checkout initiated with", {
          cartId: cart?.id,
          cartTotal: cart?.sub_total_in_currency,
          itemCount,
        });

        // Show a demo message to the user that would normally be redirected
        alert(
          "Demo Mode: In production, you would be redirected to the checkout page now.",
        );
        // Reset loading state in demo mode
        setIsCheckoutPriceLoading(false);
        return;
      }

      // Add debug logging before calling checkout
      console.log(
        `Cart widget [${widgetIdRef.current}]: Starting checkout process`,
      );

      // Call the checkout function which will internally:
      // 1. Track the CHECKOUT_STARTED event
      // 2. Flush events with beacon to ensure delivery
      // 3. Redirect to checkout page
      console.log(
        `Cart widget [${widgetIdRef.current}]: Calling checkout method`,
      );
      await checkout();

      // Note: The checkout function doesn't return a URL, it redirects automatically
      // If we reach this point, the redirection might have been blocked
      console.log(
        `Cart widget [${widgetIdRef.current}]: Checkout method completed, redirection may have been blocked`,
      );

      setIsCheckoutPriceLoading(false);
    } catch (error: unknown) {
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to checkout:`,
        error,
      );

      // Check if the error is related to missing cart token or session token
      if (error instanceof Error) {
        if (error.message.includes("cart token")) {
          console.error(
            `Cart widget [${widgetIdRef.current}]: Missing cart token - cart data:`,
            cart,
          );
        }
        if (error.message.includes("session token")) {
          console.error(
            `Cart widget [${widgetIdRef.current}]: Missing session token - check FairShare initialization`,
          );
        }
      }

      // Set loading state to false on error
      setIsCheckoutPriceLoading(false);
    } finally {
      // Reset loading state in case redirection hasn't happened
      // or the checkout process has stalled
      setTimeout(() => {
        setIsCheckoutPriceLoading(false);
      }, 5000); // Add a timeout as a safeguard if redirection is delayed
    }
  };

  // Position classes for the cart widget (for backwards compatibility)
  const positionClasses = {
    top_left: "top-0 left-0",
    top_right: "top-0 right-0",
    bottom_left: "bottom-0 left-0",
    bottom_right: "bottom-0 right-0",
  };

  // Shared cart content component to avoid duplication
  const CartContent = () => (
    <div
      className="flex flex-1 flex-col overflow-hidden"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        height: "100%",
      }}
    >
      <CartSidebarContent
        effectiveShopDisplayName={effectiveShopDisplayName}
        handleCheckout={handleCheckout}
        isCartLoading={isCartLoading}
        isCheckoutPriceLoading={isCheckoutPriceLoading}
        isAnyItemLoading={isAnyItemLoading}
        cart={cart}
        itemLoadingStates={itemLoadingStates}
        demoMode={demoMode}
        setItemLoadingStates={setItemLoadingStates}
        setCart={setCart}
        setItemCount={setItemCount}
        getTotalQuantityFromCart={getTotalQuantityFromCart}
        removeCartItemById={removeCartItemById}
        getCartWithFallback={getCartWithFallback}
        dispatchCartEvent={dispatchCartEvent}
        widgetIdRef={widgetIdRef}
        handleIncrement={handleIncrement}
        handleDecrement={handleDecrement}
        setIsCheckoutPriceLoading={setIsCheckoutPriceLoading}
        onRemoveEnrollmentPack={onRemoveEnrollmentPack}
        onEnrollmentPackAdd={onEnrollmentPackAdd}
        alwaysOpen={alwaysOpen}
      />
    </div>
  );

  // Shared footer component
  const CartFooter = () => (
    <SheetFooter className="-mx-4 -mb-0 border-t px-4 pb-4 pt-4 text-center text-sm text-muted-foreground">
      {affiliateDisplayName
        ? `Powered by ${effectiveShopDisplayName} x ${affiliateDisplayName}`
        : `Powered by ${effectiveShopDisplayName}`}
    </SheetFooter>
  );

  return (
    <>
      <ShadowDOMWrapper
        className="h-full w-full"
        shadowRootRef={shadowRootRef}
        primaryColor={primaryColor}
      >
        {/* Don't render content until settings are loaded to prevent flicker */}
        {!isLoading &&
          (alwaysOpen ? (
            // When alwaysOpen is true, render a div that matches SheetContent styling
            <div
              className="flex h-full w-full max-w-md flex-col justify-between overflow-hidden bg-background px-4 pb-0 pt-4 shadow-lg"
              style={{
                width: "450px",
              }}
            >
              <CartContent />
              <CartFooter />
            </div>
          ) : (
            // When alwaysOpen is false, render the normal Sheet with trigger
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              {!hideWidget && (
                <SheetTrigger
                  className={`fixed border-none ${positionClasses[position]} ${
                    shouldShowAnimationRef.current ? "widget-slide-in" : ""
                  } z-30 flex translate-y-0 transform cursor-pointer items-center justify-center rounded-full bg-primary text-white shadow-md transition-transform duration-300 ease-in-out`}
                  style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    margin: `${yMargin}px ${xMargin}px`,
                  }}
                  aria-label="Open cart"
                  data-fluid-trigger="true"
                  icon={
                    <div className="relative flex items-center justify-center">
                      {/* Cart icon */}
                      <ShoppingCart className="h-6 w-6" />

                      {/* Item count badge */}
                      {itemCount > 0 && (
                        <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold text-primary">
                          {itemCount}
                        </span>
                      )}
                    </div>
                  }
                />
              )}

              <SheetContent
                className={
                  stableIsMobile
                    ? "fixed bottom-0 left-0 right-0 z-[9999] flex h-[90vh] max-h-[90vh] w-full flex-col overflow-hidden rounded-t-lg bg-background px-4 pb-0 pt-4 shadow-lg"
                    : "fixed bottom-0 right-0 top-0 z-[9999] flex h-full w-full max-w-md flex-col overflow-hidden bg-background px-4 pb-0 pt-4 shadow-lg"
                }
                side={stableIsMobile ? "bottom" : "right"}
                containerRoot={shadowRootRef.current ?? undefined}
              >
                <CartContent />
                <CartFooter />
              </SheetContent>
            </Sheet>
          ))}
      </ShadowDOMWrapper>
    </>
  );
};

export default CartWidget;
